import React from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, PieChart, Pie, Cell } from 'recharts';
import { TokenUsageRecord } from '../../../../services/analyticsService';
import { UserStat } from '../../../../services/adminService';
import { format } from 'date-fns';

interface TokenUsageAnalyticsProps {
    data: TokenUsageRecord[];
    dailyData: Array<{ date: string; tokens: number; cost: number }>;
    users?: UserStat[];
}

const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#14B8A6', '#F97316'];

const TokenUsageAnalytics: React.FC<TokenUsageAnalyticsProps> = ({ data, dailyData, users = [] }) => {
    const totalTokens = data.reduce((sum, d) => sum + (d.total_tokens ?? d.input_tokens + d.output_tokens), 0);
    const totalCost = data.reduce((sum, d) => sum + d.cost_usd, 0);
    const avgCostPerToken = totalTokens > 0 ? totalCost / totalTokens : 0;

    const nameByUserId = new Map(users.map((u) => [u.id, u.full_name || u.email || u.id]));
    const displayName = (userId: string) => {
        const name = nameByUserId.get(userId);
        if (name) return name;
        return `${userId.substring(0, 8)}...`; // usuário fora da lista atual (ex: fora dos 50 mais recentes)
    };

    // Group by user_id for pie chart
    const costByUser = data.reduce((acc, record) => {
        acc[record.user_id] = (acc[record.user_id] || 0) + record.cost_usd;
        return acc;
    }, {} as Record<string, number>);

    const pieData = Object.entries(costByUser)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 8)
        .map(([userId, cost]) => ({
            name: displayName(userId),
            value: parseFloat(cost.toFixed(6))
        }));

    const chartData = dailyData.map(d => ({
        date: format(new Date(d.date), 'dd/MM'),
        Tokens: d.tokens,
        'Custo (USD)': parseFloat((d.cost * 1000).toFixed(6)) // Show in thousands for readability
    }));

    return (
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-700">
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-6">
                ⚡ Análise de Uso de Tokens
            </h3>

            {/* Summary Stats */}
            <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-4">
                    <p className="text-sm text-gray-600 dark:text-gray-400">Total de Tokens</p>
                    <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">{totalTokens.toLocaleString()}</p>
                </div>
                <div className="bg-orange-50 dark:bg-orange-900/20 rounded-lg p-4">
                    <p className="text-sm text-gray-600 dark:text-gray-400">Custo Total</p>
                    <p className="text-2xl font-bold text-orange-600 dark:text-orange-400">
                        ${totalCost.toFixed(4)}
                    </p>
                </div>
                <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
                    <p className="text-sm text-gray-600 dark:text-gray-400">Custo Médio/Token</p>
                    <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                        ${(avgCostPerToken * 1000000).toFixed(2)}/M
                    </p>
                </div>
            </div>

            {/* Charts Row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                {/* Area Chart */}
                <div>
                    <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Uso ao Longo do Tempo</h4>
                    {chartData.length > 0 ? (
                        <ResponsiveContainer width="100%" height={250}>
                            <AreaChart data={chartData}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                                <XAxis dataKey="date" stroke="#9CA3AF" style={{ fontSize: 12 }} />
                                <YAxis stroke="#9CA3AF" style={{ fontSize: 12 }} />
                                <Tooltip
                                    contentStyle={{
                                        backgroundColor: '#1F2937',
                                        border: '1px solid #374151',
                                        borderRadius: '8px',
                                        fontSize: 12
                                    }}
                                />
                                <Legend wrapperStyle={{ fontSize: 12 }} />
                                <Area type="monotone" dataKey="Tokens" stroke="#8B5CF6" fill="#8B5CF6" fillOpacity={0.6} />
                            </AreaChart>
                        </ResponsiveContainer>
                    ) : (
                        <div className="text-center py-12 text-gray-500 dark:text-gray-400 text-sm">
                            Nenhum dado disponível
                        </div>
                    )}
                </div>

                {/* Pie Chart */}
                <div>
                    <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Top Usuários por Custo</h4>
                    {pieData.length > 0 ? (
                        <ResponsiveContainer width="100%" height={250}>
                            <PieChart>
                                <Pie
                                    data={pieData}
                                    cx="50%"
                                    cy="50%"
                                    labelLine={false}
                                    label={({ name, percent }: { name?: string; percent?: number }) => `${name || 'Unknown'}: ${((percent || 0) * 100).toFixed(0)}%`}
                                    outerRadius={80}
                                    fill="#8884d8"
                                    dataKey="value"
                                >
                                    {pieData.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                    ))}
                                </Pie>
                                <Tooltip
                                    contentStyle={{
                                        backgroundColor: '#1F2937',
                                        border: '1px solid #374151',
                                        borderRadius: '8px',
                                        fontSize: 12
                                    }}
                                    formatter={(value: number | undefined) => value ? `$${value.toFixed(6)}` : '$0.000000'}
                                />
                            </PieChart>
                        </ResponsiveContainer>
                    ) : (
                        <div className="text-center py-12 text-gray-500 dark:text-gray-400 text-sm">
                            Nenhum dado disponível
                        </div>
                    )}
                </div>
            </div>

            {/* Recent Usage Table */}
            <div className="overflow-x-auto">
                <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Uso Recente</h4>
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-700/50">
                        <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                Tipo
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                Input Tokens
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                Output Tokens
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                Total
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                Modelo
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                Custo
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                Data
                            </th>
                        </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                        {data.slice(0, 15).map((usage) => (
                            <tr key={usage.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                                <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                                    {usage.usage_type}
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                                    {usage.input_tokens.toLocaleString()}
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                                    {usage.output_tokens.toLocaleString()}
                                </td>
                                <td className="px-4 py-3 text-sm text-purple-600 dark:text-purple-400 font-medium">
                                    {(usage.total_tokens ?? usage.input_tokens + usage.output_tokens).toLocaleString()}
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                                    {usage.model_name}
                                </td>
                                <td className="px-4 py-3 text-sm text-orange-600 dark:text-orange-400 font-medium">
                                    ${usage.cost_usd.toFixed(6)}
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                                    {format(new Date(usage.created_at), 'dd/MM/yyyy HH:mm')}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {data.length === 0 && (
                    <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                        Nenhum uso de tokens encontrado
                    </div>
                )}
            </div>
        </div>
    );
};

export default TokenUsageAnalytics;
